// ForgotPasswordPage - S48.4.
// Vista publica para iniciar el flujo de recuperacion de contrasena.
// Llama POST /api/auth/forgot-password (SMTP-v2) y muestra siempre el
// mismo mensaje de exito sin revelar si el email existe en el sistema.
// Estilo XL coherente con el resto del sistema (XLInput + xl-btn).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import XLInput from '../../components/xl/XLInput';
import { ensureKeyframes, injectCSS } from '../../design/motion';

const CSS_ID = 'xl-auth-pages-css';
const CSS = `
.xl-auth-page {
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  background: var(--xl-color-surface-alt);
  font-family: var(--xl-font-sans); color: var(--xl-color-text);
  padding: 24px;
}
.xl-auth-card {
  width: 100%; max-width: 420px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-md);
  padding: 28px 28px 22px;
  display: flex; flex-direction: column; gap: 16px;
}
.xl-auth-title { margin: 0; font-size: 22px; font-weight: 600 }
.xl-auth-subtitle {
  margin: -8px 0 0; font-size: 13px; color: var(--xl-color-text-secondary);
}
.xl-auth-banner { padding: 10px 14px; border-radius: var(--xl-radius-md); font-size: 13px }
.xl-auth-banner--ok  { background: rgba(5,150,105,0.10);  color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-auth-banner--err { background: rgba(220,38,38,0.10);  color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }
.xl-auth-btn {
  font: 500 14px/1 var(--xl-font-sans); padding: 12px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid var(--xl-color-primary);
  background: var(--xl-color-primary); color: var(--xl-color-text-inverse);
  cursor: pointer; transition: background 120ms cubic-bezier(0,0,0.2,1);
}
.xl-auth-btn:hover { background: var(--xl-color-primary-hover) }
.xl-auth-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-auth-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-auth-link {
  text-align: center; font: 500 13px/1.4 var(--xl-font-sans);
  color: var(--xl-color-text-secondary); text-decoration: none;
  padding: 6px 0;
}
.xl-auth-link:hover { color: var(--xl-color-text); text-decoration: underline }
`;

const SUCCESS_MESSAGE = 'Si el correo existe, se ha enviado un enlace de recuperacion.';

export default function ForgotPasswordPage() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    const v = email.trim();
    if (!v) {
      setMsg({ kind: 'err', text: 'Ingresa tu email' });
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post('/auth/forgot-password', { email: v.toLowerCase() });
      setMsg({ kind: 'ok', text: SUCCESS_MESSAGE });
    } catch (err) {
      // Mantener el mensaje uniforme aun en errores de red para no filtrar
      // existencia de cuenta. Logueamos el detalle en consola para soporte.
      // eslint-disable-next-line no-console
      console.warn('forgot-password error:', err && err.message);
      setMsg({ kind: 'ok', text: SUCCESS_MESSAGE });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="xl-auth-page">
      <form onSubmit={handleSubmit} className="xl-auth-card" noValidate>
        <h1 className="xl-auth-title">Recuperar contrasena</h1>
        <p className="xl-auth-subtitle">Te enviaremos un enlace para restablecer tu contrasena.</p>
        {msg && (
          <div
            className={'xl-auth-banner xl-auth-banner--' + (msg.kind === 'ok' ? 'ok' : 'err')}
            role={msg.kind === 'ok' ? 'status' : 'alert'}
          >
            {msg.text}
          </div>
        )}
        <XLInput
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
          required
          placeholder="tucorreo@dominio.com"
          disabled={submitting}
        />
        <button type="submit" className="xl-auth-btn" disabled={submitting}>
          {submitting ? 'Enviando...' : 'Enviar enlace de recuperacion'}
        </button>
        <Link to="/login" className="xl-auth-link">Volver al login</Link>
      </form>
    </div>
  );
}
